import { useState, useEffect, useRef, useCallback } from 'react'
import Layout from '../../components/Layout'
import { getStatementV2 } from '../../services/ReportService'
import { searchByUsername } from '../../services/api'
import Toast from '../../utils/toast'
import { SPORTS_LIST, GAME_TYPES } from '../../config/sportsList'
import { CASINO_LIST } from '../../config/casinoList'
import { getStatementBetResult, getStatementLiveCasino } from '../../services/ReportService'
import { getDetailResults, getCasinoGameDetails } from '../../apiservices/CasionApi'
import StatementDetailsModal, { normalizeSportsBet, fmtDateTime } from '../../components/StatementResultModal/StatementDetailsModal'
import { mapCasinoBet } from '../../components/StatementResultModal/mapCasinoBet'
// Casino round ke cards: user site (frontend) ke hi result renderers (vite alias @userfe)
import CasinoResultContent from '@userfe/components/casino/common/tableLayout/CasinoResultContent.jsx'
import LiveCasinoStatementModal from '@userfe/components/casino/common/tableLayout/LiveCasinoStatementModal.jsx'
import '../../components/StatementResultModal/userResult.css'
// User site jaisa: Super Over family sports-style Result modal me dikhti hai
import { isSuperOverStatementGame, buildSuperOverStatement } from '@userfe/components/casino/common/superOverStatement.js'
import '../../components/StatementResultModal/statementResult.css'
import './style.css'

// Server ki statementv2 route par maxDateRange(7) lagi hai
const MAX_RANGE_DAYS = 7

// [value, label] — value hi server ka reportType hai
const ACCOUNT_TYPES = [
  ['all', 'All'],
  ['transaction', 'Deposit/Withdraw Report'],
  ['sports', 'Sports Report'],
  ['casino', 'Casino Report'],
  ['thirdparty', 'Third Party Casino Report'],
]
const REPORT_TYPE = {
  all: 'ALL',
  transaction: 'TRANSACTION',
  sports: 'SPORTS',
  casino: 'CASINO',
  thirdparty: 'THIRD_PARTY',
}

/**
 * Ek ledger row ko table ke columns me dhaalta hai.
 * TRANSACTION rows credit/debit ko string me bhejte hain ('-' ya '12.00'),
 * SPORTS rows numbers me (0 jab lagu na ho) — dono ko yahan ek shape me laate
 * hain taaki table ek hi tarah render kare.
 */
const mapRow = (r) => {
  const num = (v) => {
    if (v === null || v === undefined || v === '-' || v === '') return 0
    const n = parseFloat(v)
    return Number.isFinite(n) ? n : 0
  }
  const credit = num(r.credit)
  const debit = num(r.debit)
  // Game row jisme net P/L 0 hai (ek bet favour, ek unfavour — Sic Bo etc.):
  // server credit 0 + debit 0 bhejta hai. Original me aisi row par Credit me
  // "0" dikhta hai, blank nahi. Opening pts / Live Casino rows ('-' ya
  // isOpening) par pehle jaisa blank.
  const isNum = (v) => v !== null && v !== undefined && v !== '-' && v !== '' && Number.isFinite(parseFloat(v))
  const netZero = !r.isOpening && isNum(r.credit) && isNum(r.debit) && credit === 0 && debit === 0
  return {
    date: r.date,
    credit: credit ? credit.toFixed(2) : (netZero ? '0' : ''),
    debit: debit ? debit.toFixed(2) : '',
    closing: r.closing === null || r.closing === undefined || r.closing === '-'
      ? '-'
      : Number(r.closing).toFixed(2),
    description: r.description || '-',
    fromto: r.fromto || '-',
    // Result modal ke liye (server statementBetInfo): synthetic rows par sab null
    isOpening: !!r.isOpening,
    category: r.category || null,
    betId: r.betId || null,
    gameCode: r.gameCode || null,
    gamename: r.gamename || null,
    roundId: r.roundId || null,
    matchId: r.matchId || null,
    eventId: r.eventId || null,
  }
}

// "Sport -> League -> Match -> Market"; blank / "NULL" segments dropped.
function buildBreadcrumb(header, fallback = '') {
  if (!header) return fallback
  const parts = [header.sportName, header.competitionName, header.matchTitle, header.marketType].filter((p) => {
    const v = p == null ? '' : String(p).trim()
    return v && v.toLowerCase() !== 'null'
  })
  return parts.length ? parts.join(' -> ') : fallback
}

// casino = { gameType, detail } jab row casino round ki ho (cards table ke upar)
const EMPTY_DETAILS = { show: false, meta: {}, bets: [], error: null, casino: null }

/**
 * AccountStatement Component
 * 
 * Page for viewing account statements with filters and export options.
 */
function AccountStatement() {
  const [entriesPerPage] = useState(25)
  const [searchValue, setSearchValue] = useState('')
  // Account Type → server ka reportType. 'ALL' = deposit/withdraw + saare game rows,
  // baaki teen game rows ko ledger category se chhaante hain.
  const [typeVal, setTypeVal] = useState('all')
  // Account Type ke hisaab se neeche ke sections (backend keys wahi purani):
  //   all         → Game Name (sirf All, kuch nahi jaata)
  //   transaction → Game Name All/Upper/Down → statement: allcredit/creditupper/creditdown
  //   sports      → Sports List + Game Type → sportId, gameType
  //   casino      → Casino List → casinoType
  //   thirdparty  → kuch nahi
  const [gameNameVal, setGameNameVal] = useState('all')
  const [sportVal, setSportVal] = useState('0')
  const [gameTypeVal, setGameTypeVal] = useState('all')
  const [casinoVal, setCasinoVal] = useState('all')
  const [clientName, setClientName] = useState('')
  const [fromDate, setFromDate] = useState('')
  const [toDate, setToDate] = useState('')
  const [tableData, setTableData] = useState([])
  const [loading, setLoading] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totals, setTotals] = useState(null)

  const [suggestions, setSuggestions] = useState([])
  const [showSuggestions, setShowSuggestions] = useState(false)

  // Credit / Debit value par click → us row ka Result modal (frontend
  // AccountStatement jaisa): CASINO round → cards + bets, SPORTS market →
  // breadcrumb/winner + bets, baaki (deposit/withdraw) → sirf remark.
  // Data pehle load hota hai, modal bharne ke baad hi khulta hai.
  const [detailsModal, setDetailsModal] = useState(EMPTY_DETAILS)
  const [rowLoading, setRowLoading] = useState(false)
  const rowRequest = useRef(0)

  // "Live Casino" line (net of the range's third-party bets) opens the provider picker
  const isLiveCasinoRow = (r) => r.isOpening && r.description === 'Live Casino'
  const [liveCasinoOpen, setLiveCasinoOpen] = useState(false)
  const loadLiveCasino = useCallback((params) => getStatementLiveCasino({ ...params, clientName }), [clientName])

  const openRowResult = async (row) => {
    if (isLiveCasinoRow(row)) { setLiveCasinoOpen(true); return }
    if (row.isOpening) return   // "Opening pts": peeche koi bet nahi
    const cat = String(row.category || '').toUpperCase()
    const plainMeta = { path: row.description || row.fromto || '', winner: '', gameTime: fmtDateTime(row.date) }

    if (!row.betId || (cat !== 'CASINO' && cat !== 'SPORTS')) {
      setDetailsModal({ show: true, meta: plainMeta, bets: [], error: null, casino: null })
      return
    }

    const req = ++rowRequest.current
    setRowLoading(true)
    try {
      if (cat === 'CASINO') {
        // Do sources: detail-results se round ke cards/winner, bet-result se
        // us user ke is round ke bets (side / win / deleted server se).
        const [resultRes, betsRes] = await Promise.all([
          row.gameCode && row.roundId
            ? getDetailResults(row.gameCode, String(row.roundId)).catch(() => null)
            : Promise.resolve(null),
          getStatementBetResult({ betId: row.betId, category: 'CASINO' }).catch(() => null),
        ])
        // Same t1 lookup the user-site result renderers use (detail = poora response).
        const hasResult = (r) => !!(r?.data?.data?.t1 || r?.data?.t1)
        let detail = hasResult(resultRes) ? resultRes : null
        const gameType = row.gameCode || betsRes?.header?.gameCode || ''
        const mid = row.roundId || betsRes?.header?.roundId || null
        // Andar Bahar 50/150 beech round me settle hoti hai, par detail-results
        // tabhi jawab deta hai jab saare cards nikal jaayen — tab tak live feed se.
        if (!detail && mid && ['ab3', 'ab4'].includes(gameType)) {
          const live = await getCasinoGameDetails(gameType).catch(() => null)
          const raw = live?.data?.data || live?.data || {}
          if (raw.card && String(raw.mid) === String(mid)) {
            detail = { data: { data: { t1: { card: raw.card, rid: String(mid), mtime: '', rdesc: '', winnat: '' } } } }
          }
        }
        if (req !== rowRequest.current) return
        const rawBets = Array.isArray(betsRes?.bets) ? betsRes.bets : []
        // Super Over family: user site ise SPORTS market jaisa dikhata hai
        // (breadcrumb / Winner / Game Time / Back-Lay table), casino cards nahi.
        if (isSuperOverStatementGame(gameType)) {
          const so = buildSuperOverStatement({ detail, header: betsRes?.header, bets: rawBets, betId: row.betId })
          setDetailsModal({ show: true, meta: so.meta, bets: so.bets.map(normalizeSportsBet), error: null, casino: null })
          return
        }
        const gamename = row.gamename || betsRes?.header?.gamename || 'Casino'
        setDetailsModal({
          show: true,
          meta: {
            path: ['casino', gameType, `${gamename}/R.No.:${mid || ''}`].filter(Boolean).join(' -> '),
            winner: betsRes?.winner || '',
            gameTime: fmtDateTime(betsRes?.header?.gameTime || row.date),
          },
          bets: rawBets.map((b) => mapCasinoBet(b, gameType, detail?.data?.data?.t1 || detail?.data?.t1)),
          error: null,
          casino: detail ? { gameType, detail } : null,
        })
      } else {
        let res = null
        let error = null
        try {
          res = await getStatementBetResult({ betId: row.betId, category: 'SPORTS' })
          if (!res?.success) throw new Error(res?.message || 'Could not load bets.')
        } catch (err) {
          error = err?.response?.data?.message || err?.message || 'Could not load bets.'
          res = null
        }
        if (req !== rowRequest.current) return
        setDetailsModal({
          show: true,
          meta: {
            path: buildBreadcrumb(res?.header, res?.description || row.description || row.fromto || ''),
            winner: res?.winner || '',
            gameTime: fmtDateTime(res?.header?.gameTime || row.date),
          },
          bets: (res?.bets || []).map(normalizeSportsBet),
          error,
          casino: null,
        })
      }
    } catch (error) {
      console.error('Error loading row result:', error)
      Toast.fire({ icon: 'error', title: error?.response?.data?.message || 'Failed to load result' })
    } finally {
      if (req === rowRequest.current) setRowLoading(false)
    }
  }
  
  const fromDateInputRef = useRef(null)
  const toDateInputRef = useRef(null)

  // Helper function to format date as YYYY-MM-DD
  const formatDate = (date) => {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
  }

  // Initialize dates: fromDate = 7 days ago, toDate = today
  const getInitialDates = () => {
    const today = new Date()
    const weekAgo = new Date()
    weekAgo.setDate(today.getDate() - 7)
    return {
      from: formatDate(weekAgo),
      to: formatDate(today)
    }
  }

  // Initialize dates on component mount
  useEffect(() => {
    const dates = getInitialDates()
    setFromDate(dates.from)
    setToDate(dates.to)
  }, [])

  // Fetch data when dependencies change
  useEffect(() => {
    if (fromDate && toDate) {
      fetchData(currentPage)
    }
  }, [entriesPerPage, currentPage])

  // Handle calendar icon click to open date picker
  const handleCalendarIconClick = (inputRef) => {
    if (inputRef && inputRef.current) {
      inputRef.current.showPicker?.() || inputRef.current.click()
    }
  }

  // fromDate → toDate ke beech kitne din
  const rangeDays = () => {
    if (!fromDate || !toDate) return 0
    return (new Date(`${toDate}T00:00:00`) - new Date(`${fromDate}T00:00:00`)) / 86400000
  }

  // Account Type ke hisaab se sirf wahi keys jo us type me lagti hain
  const extraParams = () => {
    if (typeVal === 'transaction') {
      const statement = { all: 'allcredit', upper: 'creditupper', down: 'creditdown' }[gameNameVal] || 'allcredit'
      return { statement }
    }
    if (typeVal === 'sports') return { sportId: sportVal, gameType: gameTypeVal }
    if (typeVal === 'casino') return { casinoType: casinoVal }
    return {}
  }

  // Type badalne par uske neeche wale filters default par
  const handleTypeChange = (val) => {
    setTypeVal(val)
    setGameNameVal('all')
    setSportVal('0')
    setGameTypeVal('all')
    setCasinoVal('all')
  }

  // Fetch Data
  const fetchData = async (page = 1) => {
    if (!fromDate || !toDate) return
    // Server 7 din se lambi range reject karta hai — request bhejne ka fayda nahi
    if (rangeDays() > MAX_RANGE_DAYS) return

    setLoading(true)
    try {
      const response = await getStatementV2({
        reportType: REPORT_TYPE[typeVal] || 'ALL',
        clientName,
        fromDate,
        toDate,
        page: page,
        limit: entriesPerPage,
        ...extraParams(),
      });

      if (response.success) {
        setTableData((response.data || []).map(mapRow));
        setTotalPages(response.totalPages || 1);
        // statementv2 totals nahi lautata — footer isi liye chhup jaata hai
        setTotals(response.totals || null);
        // Ensure currentPage is synced if backend returns a different page
        if (response.currentPage && response.currentPage !== page) {
            setCurrentPage(response.currentPage);
        }
      } else {
        setTableData([]);
        setTotalPages(1);
        setTotals(null);
      }
    } catch (error) {
      console.error('Error loading data:', error)
      Toast.fire({
        icon: 'error',
        title: error?.response?.data?.message || 'Failed to load account statement'
      })
      setTableData([])
      setTotalPages(1)
      setTotals(null)
    } finally {
      setLoading(false)
    }
  }

  // Handle Load button click (Reset to page 1)
  const handleLoad = () => {
    if (rangeDays() > MAX_RANGE_DAYS) {
      Toast.fire({
        icon: 'error',
        title: `Date range cannot be more than ${MAX_RANGE_DAYS} days.`
      })
      setTableData([])
      setTotalPages(1)
      return
    }
    setCurrentPage(1);
    if (currentPage === 1) {
        fetchData(1);
    }
  }

  // Handle Pagination
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  }

  // Handle PDF export (using window.print)
  const handleExportPDF = () => {
    window.print();
  }

  // Handle Excel export (using CSV)
  const handleExportExcel = () => {
    const headers = ["Date", "Credit", "Debit", "Closing", "Description", "From/To"];
    const rows = tableData.map(row => [
      new Date(row.date).toLocaleString().replace(/,/g, ''), // Remove commas to avoid CSV issues
      row.credit,
      row.debit,
      row.closing || '-',
      (row.description || '-').replace(/,/g, ' '), // Replace commas in description
      row.fromto || '-'
    ]);

    const csvContent = "data:text/csv;charset=utf-8," 
        + headers.join(",") + "\n" 
        + rows.map(e => e.join(",")).join("\n");

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `account_statement_${formatDate(new Date())}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  return (
    <Layout title="Account Statement - Diamond Admin">
      <div className="listing-grid">
        <div className="account-list-statement">
          {/* Page Title */}
          <div className="row">
            <div className="col-12">
              <div className="page-title-box d-flex align-items-center justify-content-between">
                <h4 className="mb-0 font-size-18">
                  Account Statement
                </h4>
              </div>
            </div>
          </div>

          {/* Filter Form */}
          <div className="row">
            <div className="col-12">
              <div className="">
                <div className="">
                  <form method="post" className="ajaxFormSubmit" onSubmit={(e) => { e.preventDefault(); handleLoad(); }}>
                    <div className="row row5">
                      <div className="col-md-2">
                        <label>Account Type:</label>
                        <select
                          className="form-control"
                          value={typeVal}
                          onChange={(e) => handleTypeChange(e.target.value)}
                        >
                          {ACCOUNT_TYPES.map(([val, label]) => (
                            <option key={val} value={val}>{label}</option>
                          ))}
                        </select>
                      </div>
                      {(typeVal === 'all' || typeVal === 'transaction') && (
                        <div className="col-md-2">
                          <label>Game Name:</label>
                          <select
                            className="form-control"
                            value={gameNameVal}
                            onChange={(e) => setGameNameVal(e.target.value)}
                          >
                            <option value="all">All</option>
                            {typeVal === 'transaction' && (
                              <>
                                <option value="upper">Upper</option>
                                <option value="down">Down</option>
                              </>
                            )}
                          </select>
                        </div>
                      )}
                      {typeVal === 'sports' && (
                        <>
                          <div className="col-md-2">
                            <label>Sports List:</label>
                            <select
                              className="form-control"
                              value={sportVal}
                              onChange={(e) => setSportVal(e.target.value)}
                            >
                              <option value="0">All</option>
                              {SPORTS_LIST.map(([id, name]) => (
                                <option key={id} value={id}>{name}</option>
                              ))}
                            </select>
                          </div>
                          <div className="col-md-2">
                            <label>Game Type:</label>
                            <select
                              className="form-control"
                              value={gameTypeVal}
                              onChange={(e) => setGameTypeVal(e.target.value)}
                            >
                              <option value="all">All</option>
                              {GAME_TYPES.map(([id, name]) => (
                                <option key={id} value={id}>{name}</option>
                              ))}
                            </select>
                          </div>
                        </>
                      )}
                      {typeVal === 'casino' && (
                        <div className="col-md-2">
                          <label>Casino List:</label>
                          <select
                            className="form-control"
                            value={casinoVal}
                            onChange={(e) => setCasinoVal(e.target.value)}
                          >
                            <option value="all">All</option>
                            {CASINO_LIST.map(([id, name]) => (
                              <option key={id} value={id}>{name}</option>
                            ))}
                          </select>
                        </div>
                      )}
                      <div className="col-md-2">
                        <label>Search By Client Name:</label>
                        <div className="position-relative">
                          <input 
                            type="text"
                            className="form-control"
                            placeholder="Type to search..."
                            value={clientName}
                            onChange={(e) => {
                              setClientName(e.target.value);
                              if (e.target.value.length > 1) {
                                searchByUsername(e.target.value).then(res => {
                                  if (res.success) {
                                    setSuggestions(res.data.results);
                                    setShowSuggestions(true);
                                  }
                                });
                              } else {
                                setSuggestions([]);
                                setShowSuggestions(false);
                              }
                            }}
                            onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                            onFocus={() => clientName.length > 1 && setShowSuggestions(true)}
                          />
                          {showSuggestions && suggestions.length > 0 && (
                            <ul className="list-group position-absolute w-100" style={{ zIndex: 1000, maxHeight: '200px', overflowY: 'auto' }}>
                              {suggestions.map((user) => (
                                <li 
                                  key={user.wallet_id} 
                                  className="list-group-item list-group-item-action cursor-pointer"
                                  onMouseDown={(e) => {
                                    // Use onMouseDown to prevent onBlur from firing before click
                                    e.preventDefault();
                                    setClientName(user.username);
                                    setShowSuggestions(false);
                                  }}
                                >
                                  {user.username} {user.user_type ? `(${user.user_type})` : ''}
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      </div>
                      <div className="col-md-2">
                        <label>From:</label>
                        <div className="mx-datepicker">
                          <div className="mx-input-wrapper">
                            <input 
                              ref={fromDateInputRef}
                              name="fromDate" 
                              type="date" 
                              autoComplete="off" 
                              placeholder="" 
                              className="mx-input form-control"
                              value={fromDate}
                              onChange={(e) => setFromDate(e.target.value)}
                            />
                            <i 
                              className="mx-icon-calendar"
                              onClick={() => handleCalendarIconClick(fromDateInputRef)}
                              style={{ cursor: 'pointer', pointerEvents: 'auto' }}
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1024 1024" width="1em" height="1em">
                                <path d="M940.218182 107.054545h-209.454546V46.545455h-65.163636v60.50909H363.054545V46.545455H297.890909v60.50909H83.781818c-18.618182 0-32.581818 13.963636-32.581818 32.581819v805.236363c0 18.618182 13.963636 32.581818 32.581818 32.581818h861.090909c18.618182 0 32.581818-13.963636 32.581818-32.581818V139.636364c-4.654545-18.618182-18.618182-32.581818-37.236363-32.581819zM297.890909 172.218182V232.727273h65.163636V172.218182h307.2V232.727273h65.163637V172.218182h176.872727v204.8H116.363636V172.218182h181.527273zM116.363636 912.290909V442.181818h795.927273v470.109091H116.363636z"></path>
                              </svg>
                            </i>
                          </div>
                        </div>
                      </div>
                      <div className="col-md-2">
                        <label>To:</label>
                        <div className="mx-datepicker">
                          <div className="mx-input-wrapper">
                            <input 
                              ref={toDateInputRef}
                              name="toDate" 
                              type="date" 
                              autoComplete="off" 
                              placeholder="" 
                              className="mx-input form-control"
                              value={toDate}
                              onChange={(e) => setToDate(e.target.value)}
                            />
                            <i 
                              className="mx-icon-calendar"
                              onClick={() => handleCalendarIconClick(toDateInputRef)}
                              style={{ cursor: 'pointer', pointerEvents: 'auto' }}
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1024 1024" width="1em" height="1em">
                                <path d="M940.218182 107.054545h-209.454546V46.545455h-65.163636v60.50909H363.054545V46.545455H297.890909v60.50909H83.781818c-18.618182 0-32.581818 13.963636-32.581818 32.581819v805.236363c0 18.618182 13.963636 32.581818 32.581818 32.581818h861.090909c18.618182 0 32.581818-13.963636 32.581818-32.581818V139.636364c-4.654545-18.618182-18.618182-32.581818-37.236363-32.581819zM297.890909 172.218182V232.727273h65.163636V172.218182h307.2V232.727273h65.163637V172.218182h176.872727v204.8H116.363636V172.218182h181.527273zM116.363636 912.290909V442.181818h795.927273v470.109091H116.363636z"></path>
                              </svg>
                            </i>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="row row5 mt-2">
                      <div className="col-md-12">
                        <button type="submit" className="btn btn-primary mr-2">
                          Load
                        </button>
                      
                      </div>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>

          {/* Table Section */}
          <div className="row">
            <div className="col-12">
              <div className="">
                <div className="">
                  {/* Show Entries and Search */}
                  <div className="row mt-4">
                    <div className="col-sm-12 col-md-6">
                      <div className="d-flex align-items-center gap-2">
                      <button 
                          type="button" 
                          className="btn buttons-pdf btn-danger mr-2"
                          onClick={handleExportPDF}
                          disabled={tableData.length === 0}
                        >
                          <i className="far fa-file-pdf mr-1"></i>
                          PDF
                        </button>
                        <button 
                          type="button" 
                          className="btn buttons-excel btn-success"
                          onClick={handleExportExcel}
                          disabled={tableData.length === 0}
                        >
                          <i className="far fa-file-excel mr-1"></i>
                          Excel
                        </button>
                      </div>
                    </div>
                    <div className="col-sm-12 col-md-6">
                      <div id="tickets-table_filter" className="dataTables_filter text-md-right">
                        <label className="d-inline-flex align-items-center">
                          Search:
                          <input 
                            name="search" 
                            type="search" 
                            placeholder="Search..." 
                            className="form-control form-control-sm ml-2 form-control py-1"
                            value={searchValue}
                            onChange={(e) => setSearchValue(e.target.value)}
                          />
                        </label>
                      </div>
                    </div>
                  </div>

                  {/* Table */}
                  <div className="table-responsive mb-0 mt-4" style={rowLoading ? { position: 'relative' } : undefined}>
                    {/* Row ka result is spinner ke peeche load hota hai; table waisi hi rehti hai, modal bharne par khulta hai */}
                    {rowLoading && (
                      <div className="text-center" style={{ position: 'absolute', inset: 0, zIndex: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(255,255,255,0.5)' }}>
                        <i className="fa fa-spinner fa-spin"></i>
                      </div>
                    )}
                    <div className="table no-footer table-responsive-sm">
                      <table 
                        id="accountStatementTbl" 
                        role="table" 
                        aria-busy={loading}
                        aria-colcount="6"
                        className="table b-table table-striped table-bordered"
                      >
                        <thead role="rowgroup" className="">
                          <tr role="row" className="">
                            <th role="columnheader" scope="col" className="position-relative"><div>Date</div></th>
                            <th role="columnheader" scope="col" className="position-relative"><div>Credit</div></th>
                            <th role="columnheader" scope="col" className="position-relative"><div>Debit</div></th>
                            <th role="columnheader" scope="col" className="position-relative"><div>Closing</div></th>
                            <th role="columnheader" scope="col" className="position-relative"><div>Description</div></th>
                            <th role="columnheader" scope="col" className="position-relative"><div>From/To</div></th>
                          </tr>
                        </thead>
                        <tbody role="rowgroup">
                          {loading ? (
                            <tr role="row" className="b-table-empty-row">
                              <td colSpan="6" role="cell" className="">
                                <div role="alert" aria-live="polite">
                                  <div className="text-center my-2">Loading...</div>
                                </div>
                              </td>
                            </tr>
                          ) : tableData.length === 0 ? (
                            <tr role="row" className="b-table-empty-row">
                              <td colSpan="6" role="cell" className="">
                                <div role="alert" aria-live="polite">
                                  <div className="text-center my-2">No data available in table</div>
                                </div>
                              </td>
                            </tr>
                          ) : (
                            tableData
                              .filter(row => 
                                !searchValue || 
                                Object.values(row).some(val => 
                                  String(val).toLowerCase().includes(searchValue.toLowerCase())
                                )
                              )
                              .map((row, index) => (
                              <tr
                                key={row.id || index}
                                role="row"
                                className={!row.isOpening || isLiveCasinoRow(row) ? 'stmt-row-link' : ''}
                                onClick={(!row.isOpening || isLiveCasinoRow(row)) && !rowLoading ? () => openRowResult(row) : undefined}
                              >
                                <td aria-colindex="1" role="cell" className="">
                                  {new Date(row.date).toLocaleString() || '-'}
                                </td>
                                <td aria-colindex="2" role="cell" className="" style={{color: 'green'}}>
                                  {row.credit}
                                </td>
                                <td aria-colindex="3" role="cell" className="" style={{color: 'red'}}>
                                  {row.debit}
                                </td>
                                <td aria-colindex="4" role="cell" className="">
                                  {row.closing || '-'}
                                </td>
                                <td aria-colindex="5" role="cell" className="">
                                  {row.description ? (
                                    <span className="wrape-text">{row.description}</span>
                                  ) : '-'}
                                </td>
                                <td aria-colindex="6" role="cell" className="">
                                  {row.fromto || '-'}
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                        {!loading && totals && tableData.length > 0 && (
                          <tfoot>
                            <tr role="row" style={{ fontWeight: 700, borderTop: '2px solid #333' }}>
                              <td role="cell" style={{ textAlign: 'right' }}>Opening: {Number(totals.opening).toFixed(2)}</td>
                              <td role="cell" style={{ color: 'green' }}>{Number(totals.totalCredit).toFixed(2)}</td>
                              <td role="cell" style={{ color: 'red' }}>{Number(totals.totalDebit).toFixed(2)}</td>
                              <td role="cell">{Number(totals.closing).toFixed(2)}</td>
                              <td role="cell" colSpan="2" style={{ textAlign: 'right' }}>
                                Net P/L:&nbsp;
                                <span style={{ color: Number(totals.netPL) >= 0 ? 'green' : 'red' }}>
                                  {Number(totals.netPL).toFixed(2)}
                                </span>
                              </td>
                            </tr>
                          </tfoot>
                        )}
                      </table>
                    </div>
                  </div>

                  {/* Pagination */}
                  <div className="row pt-3">
                    <div className="col">
                      <div className="dataTables_paginate paging_simple_numbers float-right">
                        <ul className="pagination pagination-rounded mb-0">
                          <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                            <button className="page-link" onClick={() => handlePageChange(1)}>«</button>
                          </li>
                          <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                            <button className="page-link" onClick={() => handlePageChange(currentPage - 1)}>‹</button>
                          </li>
                          
                          <li className="page-item active">
                            <span className="page-link">{currentPage} / {totalPages}</span>
                          </li>

                          <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                            <button className="page-link" onClick={() => handlePageChange(currentPage + 1)}>›</button>
                          </li>
                          <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                            <button className="page-link" onClick={() => handlePageChange(totalPages)}>»</button>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Har row ka "Details" modal (original admin jaisa); casino round par upar cards */}
      <StatementDetailsModal
        show={detailsModal.show}
        onHide={() => setDetailsModal(EMPTY_DETAILS)}
        meta={detailsModal.meta}
        bets={detailsModal.bets}
        error={detailsModal.error}
      >
        {detailsModal.casino && (
          <div className="stmt-user-result">
            <CasinoResultContent gameType={detailsModal.casino.gameType} detailData={detailsModal.casino.detail} bets={detailsModal.bets} statement />
          </div>
        )}
      </StatementDetailsModal>

      <LiveCasinoStatementModal
        show={liveCasinoOpen}
        onHide={() => setLiveCasinoOpen(false)}
        fromDate={fromDate}
        toDate={toDate}
        load={loadLiveCasino}
      />
    </Layout>
  )
}

export default AccountStatement
